import { useEffect, useRef, useState } from "react";
import { loadTurnstile, type TurnstileApi } from "./turnstile";

type Props = {
  siteKey: string;
  /** Changes whenever a fresh token is needed: a token can be spent only once. */
  resetToken: number;
  onToken: (token: string | null) => void;
};

export function TurnstileWidget({ siteKey, resetToken, onToken }: Props) {
  const container = useRef<HTMLDivElement>(null);
  const widget = useRef<{ api: TurnstileApi; id: string } | null>(null);
  const report = useRef(onToken);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    report.current = onToken;
  }, [onToken]);

  useEffect(() => {
    const element = container.current;
    if (!element) return;
    let cancelled = false;
    loadTurnstile().then(
      (api) => {
        if (cancelled) return;
        const id = api.render(element, {
          sitekey: siteKey,
          theme: "dark",
          callback: (token) => report.current(token),
          "expired-callback": () => report.current(null),
          "error-callback": () => report.current(null),
        });
        widget.current = { api, id };
      },
      () => {
        if (!cancelled) setFailed(true);
      },
    );
    return () => {
      cancelled = true;
      if (widget.current) widget.current.api.remove(widget.current.id);
      widget.current = null;
    };
  }, [siteKey]);

  useEffect(() => {
    if (resetToken > 0 && widget.current) widget.current.api.reset(widget.current.id);
  }, [resetToken]);

  return (
    <div className="turnstile">
      <div ref={container} />
      {failed ? (
        <p className="turnstile__failed" role="alert">
          The spam check could not be loaded, so the form cannot send. Check your connection or any
          content blocker, then reload the page.
        </p>
      ) : null}
    </div>
  );
}
